import { z } from 'zod'

export const copyLoadDetailsLegacyFieldKeys = [
  'routeStopType',
  'routeLocationName',
  'routeAddress',
  'routeDate',
  'routeTime',
  'cargoGoods',
  'cargoPackaging',
  'cargoPallets',
  'cargoWeight',
  'cargoDimensions',
  'cargoTemperature',
  'cargoRequirements',
  'vehicleType',
  'rate',
] as const

export const copyLoadDetailsFieldKeys = [
  'routeStopType',
  'routeLocationName',
  'routeAddress',
  'routeDate',
  'routeTime',
  'cargoGoods',
  'cargoPackaging',
  'cargoPallets',
  'cargoWeight',
  'cargoTemperature',
  'cargoRequirements',
  'vehicleType',
  'haulierRate',
  'shipperRate',
] as const

export const copyLoadDetailsLegacySchema = z.object({
  outputStyle: z.enum(['compact', 'detailed']),
  rateSource: z.enum(['haulier', 'shipper']),
  selectedFields: z.array(z.enum(copyLoadDetailsLegacyFieldKeys)).min(1),
  version: z.literal(1),
})

export const copyLoadDetailsSchema = z.object({
  outputStyle: z.enum(['compact', 'detailed']),
  selectedFields: z.array(z.enum(copyLoadDetailsFieldKeys)).min(1),
  version: z.literal(2),
})

export type CopyLoadDetailsFieldKey = (typeof copyLoadDetailsFieldKeys)[number]
export type CopyLoadDetailsLegacySettings = z.infer<typeof copyLoadDetailsLegacySchema>
export type CopyLoadDetailsOutputStyle = 'compact' | 'detailed'
export type CopyLoadDetailsSettings = z.infer<typeof copyLoadDetailsSchema>

export const copyLoadDetailsSettingsSchema = z.union([
  copyLoadDetailsSchema,
  copyLoadDetailsLegacySchema,
])

export const copyLoadDetailsDefaults = {
  outputStyle: 'detailed' as const,
  selectedFields: copyLoadDetailsFieldKeys.filter(
    field => field !== 'haulierRate' && field !== 'shipperRate',
  ),
  version: 2 as const,
}

const createCopyLoadDetailsDefaults = (): CopyLoadDetailsSettings => ({
  ...copyLoadDetailsDefaults,
  selectedFields: [...copyLoadDetailsDefaults.selectedFields],
})

export const normalizeCopyLoadDetails = (value: unknown): CopyLoadDetailsSettings => {
  const parsed = copyLoadDetailsSettingsSchema.safeParse(value)
  if (!parsed.success) return createCopyLoadDetailsDefaults()

  const data = parsed.data
  const selectedFieldSet = new Set(data.selectedFields)
  const selectedFields = copyLoadDetailsFieldKeys.filter(field => {
    if (data.version === 2) return selectedFieldSet.has(field)

    if (field === 'haulierRate') {
      return data.rateSource === 'haulier' && selectedFieldSet.has('rate')
    }

    if (field === 'shipperRate') {
      return data.rateSource === 'shipper' && selectedFieldSet.has('rate')
    }

    return selectedFieldSet.has(field)
  })

  return selectedFields.length
    ? {
        outputStyle: data.outputStyle,
        selectedFields,
        version: 2,
      }
    : createCopyLoadDetailsDefaults()
}

/**
 * A stored Load Detail Layout, checked for structure only: the admin app owns the card, field, and
 * column IDs and drops the ones it does not know. A malformed part parses as absent, so it falls
 * back to its default without failing the rest of the page data.
 */
export const overviewLayoutSchema = z.object({
  // Visibility per card: { cardId: { visible } }
  cards: z
    .record(z.string(), z.object({ visible: z.boolean() }))
    .optional()
    .catch(undefined),
  // Card placement per column count: { '1' | '2' | '3': { columnId: [cardIds] } }
  columns: z
    .record(z.string(), z.record(z.string(), z.array(z.string())))
    .optional()
    .catch(undefined),
  // Full field order also records the ordinary field catalog last saved by the user.
  fieldOrder: z.record(z.string(), z.array(z.string()).readonly()).optional().catch(undefined),
  // Selected fields per card, in display order: { cardId: [fieldIds] }
  fields: z.record(z.string(), z.array(z.string()).readonly()).optional().catch(undefined),
})

export type StoredOverviewLayout = z.infer<typeof overviewLayoutSchema>

export const loadDetailLayoutSchema = overviewLayoutSchema.extend({
  knownStopFields: z.array(z.string()).readonly().optional().catch(undefined),
  routeDisplayMode: z.string().optional().catch(undefined),
  stopFieldOrder: z.array(z.string()).readonly().optional().catch(undefined),
  stopFields: z.array(z.string()).readonly().optional().catch(undefined),
})

export type StoredLoadDetailLayout = z.infer<typeof loadDetailLayoutSchema>

/** Where the User duplicates a load: the dialog, or the side panel that creates one at a time. */
export const duplicateContainerSchema = z.enum(['dialog', 'panel'])

export type DuplicateContainer = z.infer<typeof duplicateContainerSchema>

export const pageDataValueSchema = z.looseObject({
  copyLoadDetails: z.unknown().optional(),
  duplicateContainer: duplicateContainerSchema.optional().catch(undefined),
  loadDetailLayout: loadDetailLayoutSchema.optional().catch(undefined),

  // Malformed table settings parse as absent, so they fall back to the defaults without failing
  // the rest of the page data (Saved Views, layouts).
  table: z
    .object({
      // Column ordering: array of column IDs in display order
      order: z.string().array().optional(),

      // Schema version for one-time forced column-order migrations
      orderVersion: z.number().optional(),

      // Column pinning: columns pinned to left/right
      pinning: z
        .object({
          left: z.array(z.string()).optional(),
          right: z.array(z.string()).optional(),
        })
        .or(z.unknown().array())
        .optional(),

      // Column sizing: { columnId: width in pixels }
      sizing: z.union([z.record(z.string(), z.number()).optional(), z.unknown().array()]),

      // Column visibility: { columnId: boolean }
      visibility: z.union([z.record(z.string(), z.boolean()).optional(), z.unknown().array()]),
    })
    .optional()
    .catch(undefined),
})

export const pageDataSchema = z.object({
  page: z.string().optional(),
  user_id: z.number().optional(),
  value: pageDataValueSchema.optional(),
})
